import { t } from "ttag";

import { useMetadataToasts } from "metabase/common/hooks";
import { Icon, Menu } from "metabase/ui";
import type { VerifyItemRequest } from "metabase-types/api";

import { useCreateModerationReviewMutation } from "../../api";

type VerifyMenuItemProps = {
  itemType: VerifyItemRequest["moderated_item_type"];
  itemId: VerifyItemRequest["moderated_item_id"];
  isVerified: boolean;
  verifyLabel: string;
  onChange?: () => void;
};

/**
 * "Verify this …" for an unverified item and "Remove verification" for a
 * verified one. Calls `onChange` once the change is saved.
 */
export function VerifyMenuItem({
  itemType,
  itemId,
  isVerified,
  verifyLabel,
  onChange,
}: VerifyMenuItemProps) {
  const [createModerationReview, { isLoading }] =
    useCreateModerationReviewMutation();
  const { sendErrorToast, sendSuccessToast } = useMetadataToasts();

  const handleClick = async () => {
    const result = await createModerationReview({
      moderated_item_id: itemId,
      moderated_item_type: itemType,
      status: isVerified ? null : "verified",
    });

    if (result.error) {
      sendErrorToast(
        isVerified
          ? t`Couldn't remove the verification`
          : t`Couldn't verify this item`,
      );
      return;
    }

    sendSuccessToast(isVerified ? t`Verification removed` : t`Verified`);
    onChange?.();
  };

  return (
    <Menu.Item
      leftSection={<Icon name={isVerified ? "close" : "verified"} />}
      disabled={isLoading}
      onClick={handleClick}
    >
      {isVerified ? t`Remove verification` : verifyLabel}
    </Menu.Item>
  );
}
